import { useState, useCallback, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Button,
  Group,
  Badge,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  ScrollArea,
  Menu,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconRefresh,
  IconFilter,
  IconCalendar,
  IconDownload,
  IconFileSpreadsheet,
  IconFileTypePdf,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useTaskStore } from "../../store/task.store";
import { useAllCorrectionRequests } from "../../hooks/useCorrectionRequests";
import { correctionRequestService } from "../../services/correctionRequestService";
import { formatDateOnlyWithShortMonth } from "../../utils/dateOnly";
import { CorrectionRow } from "./CorrectionRow";
import {
  CorrectionsQueueDateFilters,
  EMPTY_CORRECTIONS_QUEUE_FILTERS,
  type CorrectionsQueueFilters,
} from "./CorrectionsQueueDateFilters";
import { getApiErrorStatus } from "../../utils/apiError";

const ITEMS_PER_PAGE = 20;

export default function CorrectionsQueuePage() {
  const navigate = useNavigate();
  const { selectedTasks } = useTaskStore();
  const selectedSlugs = useMemo(
    () => selectedTasks.map((t) => t.slug),
    [selectedTasks]
  );

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<CorrectionsQueueFilters>(
    EMPTY_CORRECTIONS_QUEUE_FILTERS
  );

  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const queryFilters = useMemo(
    () => ({
      page,
      limit: ITEMS_PER_PAGE,
      dateFrom: filters.dateFrom ?? undefined,
      dateTo: filters.dateTo ?? undefined,
      dateReceivedFrom: filters.dateReceivedFrom ?? undefined,
      dateReceivedTo: filters.dateReceivedTo ?? undefined,
      dateCollectedFrom: filters.dateCollectedFrom ?? undefined,
      dateCollectedTo: filters.dateCollectedTo ?? undefined,
      dateReportedFrom: filters.dateReportedFrom ?? undefined,
      dateReportedTo: filters.dateReportedTo ?? undefined,
      search: debouncedSearch.trim() || undefined,
    }),
    [debouncedSearch, filters, page]
  );

  const { data, isLoading, refetch } = useAllCorrectionRequests(queryFilters);

  const allItems = useMemo(
    () => data?.correctionRequests ?? [],
    [data?.correctionRequests]
  );

  const total = useMemo(() => data?.total ?? 0, [data?.total]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)),
    [total]
  );

  const activeFilterCount = useMemo(
    () =>
      ["date", "dateReceived", "dateCollected", "dateReported"].filter(
        (name) =>
          filters[`${name}From` as keyof CorrectionsQueueFilters] ||
          filters[`${name}To` as keyof CorrectionsQueueFilters]
      ).length,
    [filters]
  );

  const handleOpenFilterModal = useCallback(() => {
    openFilterModal();
  }, [openFilterModal]);

  const handleApplyFilters = useCallback(
    (nextFilters: CorrectionsQueueFilters) => {
      setFilters(nextFilters);
      setPage(1);
      closeFilterModal();
    },
    [closeFilterModal]
  );

  const handleClearAllFilters = useCallback(() => {
    setFilters(EMPTY_CORRECTIONS_QUEUE_FILTERS);
    setPage(1);
  }, []);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleNavigateToReview = useCallback(
    (caseId: string) => {
      if (selectedSlugs.includes("pathologist-review")) {
        navigate(`/pathologist-review/${caseId}?tab=corrections`);
      } else {
        navigate(`/cases/${caseId}?tab=corrections`);
      }
    },
    [navigate, selectedSlugs]
  );

  const handleNavigateToError = useCallback(
    (caseId: string) => {
      navigate(`/cases/${caseId}?tab=errors`);
    },
    [navigate]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
      setPage(1);
    },
    []
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleExport = useCallback(
    async (format: "excel" | "pdf") => {
      try {
        await correctionRequestService.exportCorrections(format, {
          dateFrom: filters.dateFrom ?? undefined,
          dateTo: filters.dateTo ?? undefined,
          dateReceivedFrom: filters.dateReceivedFrom ?? undefined,
          dateReceivedTo: filters.dateReceivedTo ?? undefined,
          dateCollectedFrom: filters.dateCollectedFrom ?? undefined,
          dateCollectedTo: filters.dateCollectedTo ?? undefined,
          dateReportedFrom: filters.dateReportedFrom ?? undefined,
          dateReportedTo: filters.dateReportedTo ?? undefined,
          search: debouncedSearch.trim() || undefined,
        });
      } catch (error: unknown) {
        const status = getApiErrorStatus(error);
        const message =
          status === 400
            ? "The export is too large or invalid. Narrow the filters and try again."
            : status === 409
            ? "The Corrections queue changed while the export was being prepared. Please retry."
            : status === 401 || status === 403
            ? "You are not authorized to export Corrections data."
            : "The Corrections export could not be downloaded. Please try again.";
        notifications.show({
          title: "Export failed",
          message,
          color: "red",
        });
      }
    },
    [debouncedSearch, filters]
  );

  const handleExportExcel = useCallback(() => {
    void handleExport("excel");
  }, [handleExport]);

  const handleExportPdf = useCallback(() => {
    void handleExport("pdf");
  }, [handleExport]);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const filterDateLabel = useMemo(() => {
    if (filters.dateFrom && filters.dateTo) {
      return `${formatDateOnlyWithShortMonth(filters.dateFrom, {
        omitYear: true,
      })} – ${formatDateOnlyWithShortMonth(filters.dateTo, {
        omitYear: true,
      })}`;
    }
    if (filters.dateFrom) {
      return `From ${formatDateOnlyWithShortMonth(filters.dateFrom, {
        omitYear: true,
      })}`;
    }
    if (filters.dateTo) {
      return `To ${formatDateOnlyWithShortMonth(filters.dateTo, {
        omitYear: true,
      })}`;
    }
    return null;
  }, [filters.dateFrom, filters.dateTo]);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: 1600, margin: "0 auto" }}>
        <Stack gap="md">
          {/* Header */}
          <Group justify="space-between" align="flex-end">
            <Box>
              <Title order={2}>Corrections Queue</Title>
              <Text c="dimmed" size="sm">
                Completed corrected cases, including historical outcomes
              </Text>
            </Box>
          </Group>

          {/* Search + Filters row */}
          <Group align="stretch" gap="sm">
            <Paper withBorder p="sm" style={{ flex: 2 }}>
              <Group gap="xs">
                <IconSearch size={16} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                  Search
                </Text>
                <TextInput
                  placeholder="Patient name, case #, or correcting pathologist..."
                  value={search}
                  onChange={handleSearchChange}
                  style={{ flex: 1 }}
                  size="xs"
                  variant="unstyled"
                />
              </Group>
            </Paper>

            <Paper withBorder p="sm" style={{ flex: 1 }}>
              <Group gap="xs" justify="space-between">
                <Group gap="xs">
                  <Tooltip label="Refresh">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={handleRefresh}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Button
                    size="xs"
                    variant={activeFilterCount > 0 ? "filled" : "light"}
                    color={activeFilterCount > 0 ? "blue" : "gray"}
                    leftSection={<IconFilter size={14} />}
                    onClick={handleOpenFilterModal}
                  >
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge
                        size="xs"
                        circle
                        variant="white"
                        color="blue"
                        style={{ marginLeft: 4 }}
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                  <Menu withinPortal position="bottom-start">
                    <Menu.Target>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconDownload size={14} />}
                      >
                        Export
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Download all matching corrections</Menu.Label>
                      <Menu.Item
                        leftSection={<IconFileSpreadsheet size={14} />}
                        onClick={handleExportExcel}
                      >
                        Excel workbook
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconFileTypePdf size={14} />}
                        onClick={handleExportPdf}
                      >
                        PDF report
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                  {activeFilterCount > 0 && (
                    <Group gap="xs">
                      {(filters.dateFrom || filters.dateTo) && (
                        <Badge
                          variant="light"
                          color="grape"
                          size="sm"
                          leftSection={<IconCalendar size={10} />}
                        >
                          {filterDateLabel}
                        </Badge>
                      )}
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        color="gray"
                        onClick={handleClearAllFilters}
                      >
                        Clear All
                      </Button>
                    </Group>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {total} total
                </Text>
              </Group>
            </Paper>
          </Group>

          {/* Table */}
          <Paper withBorder>
            <Box
              p="sm"
              style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
            >
              <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>
                  Results
                </Text>
                <Pagination
                  value={page}
                  onChange={handlePageChange}
                  total={totalPages}
                  size="xs"
                />
              </Group>
            </Box>

            {isLoading ? (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            ) : allItems.length === 0 ? (
              <Center py="xl">
                <Text c="dimmed" size="sm">
                  No corrected cases found.
                </Text>
              </Center>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover style={{ minWidth: 1500 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 28 }} />
                      <Table.Th style={{ width: 56 }}>Ref</Table.Th>
                      <Table.Th>Patient</Table.Th>
                      <Table.Th>Case #</Table.Th>
                      <Table.Th>Accessioned by</Table.Th>
                      <Table.Th>Corrected By</Table.Th>
                      <Table.Th ta="center">Fields</Table.Th>
                      <Table.Th style={{ whiteSpace: "nowrap" }}>
                        Date Received
                      </Table.Th>
                      <Table.Th style={{ whiteSpace: "nowrap" }}>
                        Date Collected
                      </Table.Th>
                      <Table.Th style={{ whiteSpace: "nowrap" }}>
                        Date Reported
                      </Table.Th>
                      <Table.Th>Corrected At</Table.Th>
                      <Table.Th style={{ width: 160 }}>Outcome</Table.Th>
                      <Table.Th style={{ width: 160 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {allItems.map((item) => (
                      <CorrectionRow
                        key={item.id}
                        item={item}
                        isExpanded={expandedRows.has(item.id)}
                        onToggle={toggleRow}
                        onNavigateToReview={handleNavigateToReview}
                        onNavigateToError={handleNavigateToError}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Paper>
        </Stack>
      </Box>

      {filterModalOpened && (
        <CorrectionsQueueDateFilters
          filters={filters}
          onApply={handleApplyFilters}
          onClose={closeFilterModal}
        />
      )}
    </Container>
  );
}
